<template>
  <div>
    <v-list-item @click="isExpanded = !isExpanded">
      <div class="d-flex align-center ga-3 justify-space-between">
        <div class="d-flex ga-3 align-center">
          <v-btn
            rounded="lg"
            variant="flat"
            color="grey-lighten-3"
            size="small"
            density="comfortable"
            icon>
            <v-icon
              icon="mdi-office-building-outline"
              size="small"
              color="grey"></v-icon>
          </v-btn>
          <div>
            <div class="font-weight-bold font-size-15">
              {{ department.name }}
            </div>
            <div class="text-disabled">
              {{ department.internalCode }}
            </div>
          </div>
        </div>

        <div class="d-flex align-center ga-3">
          <v-btn
            density="compact"
            variant="flat"
            color="light-blue-darken-4"
            icon
            rounded="circle">
            <b>{{ selectedChildUserLength }}/{{ childUserIds.length }}</b>
          </v-btn>
          <v-icon
            size="small"
            color="grey"
            :icon="isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"></v-icon>
        </div>
      </div>
    </v-list-item>

    <div
      v-show="isExpanded"
      :style="{
        marginLeft: `${20 * index}px`,
      }"
      style="border-left: 3px solid #01579b">
      <div
        v-if="!department.users.length && !department.childItems.length"
        class="text-center text-disabled py-5">
        Немає людей в цьому підрозділі
      </div>
      <div
        v-if="department.users.length"
        class="px-3 py-1 d-flex align-center ga-3 bg-blue-lighten-5">
        <v-checkbox
          :model-value="
            existDepartmentIds.includes(department.id) ||
            selectedDepartments.includes(department.id)
          "
          color="light-blue-darken-4"
          hide-details
          :readonly="existDepartmentIds.includes(department.id)"
          @update:model-value="handleDepartment">
        </v-checkbox>
        <div class="font-weight-bold text-light-blue-darken-4 font-size-13">
          Додати всіх ({{ childUserIds.length }})
        </div>
      </div>

      <div v-if="department.childItems.length">
        <department-item
          v-for="child in department.childItems"
          :key="child.id"
          :department="child"
          :index="index + 1"></department-item>
      </div>

      <div v-if="department.users.length">
        <div
          v-for="user in department.users"
          :key="user.id"
          class="px-3 py-1 d-flex ga-3 align-center">
          <v-checkbox
            :model-value="
              existUserIds.includes(user.id) ||
              existDepartmentIds.includes(department.id) ||
              selectedDepartments.includes(department.id) ||
              selectedUsers.includes(user.id)
            "
            :readonly="
              existUserIds.includes(user.id) ||
              existDepartmentIds.includes(department.id) ||
              selectedDepartments.includes(department.id)
            "
            color="light-blue-darken-4"
            hide-details
            @update:model-value="handleUser(user.id)"></v-checkbox>
          <v-avatar color="indigo-lighten-5" size="30">
            <span class="text-indigo-darken-1 font-weight-bold text-subtitle-2">
              {{ user.name?.[0] + user.surname?.[0] }}
            </span>
          </v-avatar>
          <div>
            <div>
              {{ getPersonName(user) }}
            </div>
            <div class="text-disabled">{{ user.post }}</div>
          </div>
        </div>
      </div>
    </div>

    <v-divider></v-divider>
  </div>
</template>

<script setup lang="ts">
import { getPersonName } from 'best-modules/utils'
import {
  selectedUsersKey,
  selectedDepartmentsKey,
  existDepartmentIdsKey,
  existUserIdsKey,
  usersToDeleteKey,
} from './injectionKeys'
import { computed, inject, ref } from 'vue'
import { InternalDepartmentTreeItem } from '@/utils/types/internalDepartment'

defineOptions({
  name: 'DepartmentItem',
})

const props = withDefaults(
  defineProps<{
    department: InternalDepartmentTreeItem
    index?: number
  }>(),
  { index: 0 }
)

const isExpanded = ref<boolean>(false)
const selectedUsers = inject(selectedUsersKey, ref<number[]>([]))
const selectedDepartments = inject(selectedDepartmentsKey, ref<number[]>([]))
const existDepartmentIds = inject(existDepartmentIdsKey, [])
const existUserIds = inject(existUserIdsKey, [])
const usersToDelete = inject(usersToDeleteKey, ref<number[]>([]))

const childUserIds = computed<number[]>(() => {
  const getDepUsers = (dep: any): number[] => {
    const arr = [...dep.users.map((u: any) => u.id)]
    dep.childItems.forEach((d: any) => {
      arr.push(...getDepUsers(d))
    })
    return arr
  }

  return getDepUsers(props.department)
})

const childDepartmentIds = computed<number[]>(() => {
  const getDepDepartments = (dep: any): number[] => {
    const arr = [...dep.childItems.map((d: any) => d.id)]
    dep.childItems.forEach((d: any) => {
      arr.push(...getDepDepartments(d))
    })
    return arr
  }

  return getDepDepartments(props.department)
})

const getSelectedChildUserLength = (
  department: InternalDepartmentTreeItem
): number => {
  const isChecked =
    existDepartmentIds.includes(department.id) ||
    selectedDepartments.value.includes(department.id)
  let length: number = 0

  if (isChecked) {
    length = department.users.length
  } else {
    length = department.users.filter(u => {
      return existUserIds.includes(u.id) || selectedUsers.value.includes(u.id)
    }).length
  }

  department.childItems.forEach(d => {
    length += getSelectedChildUserLength(d)
  })

  return length
}

const selectedChildUserLength = computed<number>(() => {
  return getSelectedChildUserLength(props.department)
})

const pushDepartments = (...ids: number[]) => {
  for (const id of ids) {
    if (!existDepartmentIds.includes(id)) {
      selectedDepartments.value.push(id)
    }
  }
}

const removeDepartments = (...ids: number[]) => {
  for (const id of ids) {
    if (!existDepartmentIds.includes(id)) {
      selectedDepartments.value = selectedDepartments.value.filter(
        i => i !== id
      )
    }
  }
}

const pushUser = (id: number) => {
  if (!existUserIds.includes(id)) {
    selectedUsers.value.push(id)
  }
}
const removeUser = (id: number) => {
  if (!existUserIds.includes(id)) {
    selectedUsers.value = selectedUsers.value.filter(i => i !== id)
  }
}

const handleDepartment = () => {
  const existChildrenUsers = childUserIds.value.filter(id =>
    existUserIds.includes(id)
  )

  if (selectedDepartments.value.includes(props.department.id)) {
    removeDepartments(props.department.id, ...childDepartmentIds.value)
    usersToDelete.value = usersToDelete.value.filter(id => {
      return existChildrenUsers.includes(id)
    })
  } else {
    pushDepartments(props.department.id, ...childDepartmentIds.value)
    if (existChildrenUsers.length) {
      usersToDelete.value.push(...existChildrenUsers)
    }
  }
}

const handleUser = (userId: number) => {
  if (selectedUsers.value.includes(userId)) {
    removeUser(userId)
  } else {
    pushUser(userId)
  }
}
</script>
